import React, { useEffect, useRef } from 'react';
import { flameColor, flameSprite, flameSoftSprite, drawGasBlob } from '../../utils/emberPalette';

// Site-wide hover gas: converges galaxy gas into any element marked with [data-gas].
const HoverGas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = window.innerWidth;
    let h = window.innerHeight;
    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    let targetEl = null;
    let absorbGlow = 0;
    let emitCooldown = 0;
    const parts = [];

    const over = (e) => {
      const el = e.target.closest && e.target.closest('[data-gas]');
      if (el && el !== targetEl) {
        targetEl = el;
        emitCooldown = 0;
        emit(8);
      } else if (!el && targetEl && !targetEl.contains(e.target)) {
        targetEl = null;
      }
    };
    const out = (e) => {
      if (targetEl && !e.relatedTarget?.closest?.('[data-gas]')) targetEl = null;
    };
    document.addEventListener('mouseover', over);
    document.addEventListener('mouseout', out);

    const center = () => {
      if (!targetEl || !document.contains(targetEl)) {
        targetEl = null;
        return null;
      }
      const r = targetEl.getBoundingClientRect();
      return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    };

    const emit = (n) => {
      const c = center();
      if (!c) return;
      for (let i = 0; i < n; i++) {
        const ang = Math.random() * Math.PI * 2;
        const dist = 70 + Math.random() * 70;
        const x = c.x + Math.cos(ang) * dist;
        const y = c.y + Math.sin(ang) * dist;
        parts.push({
          x,
          y,
          vx: 0,
          vy: 0,
          d0: dist,
          life: 0,
          maxLife: 55 + Math.random() * 30,
          r: 0.6 + Math.random() * 1.5,
          trail: [{ x, y, j: 0 }],
        });
      }
    };

    let raf;
    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'lighter';
      const c = center();
      if (c) {
        emitCooldown -= 1;
        if (emitCooldown <= 0) {
          emit(2);
          emitCooldown = 6;
        }
        if (absorbGlow > 0.02) {
          const e = Math.min(absorbGlow, 1);
          const g = ctx.createRadialGradient(c.x, c.y, 0, c.x, c.y, 60);
          g.addColorStop(0, `rgba(255, 160, 90, ${e * 0.28})`);
          g.addColorStop(1, 'rgba(255, 160, 90, 0)');
          ctx.beginPath();
          ctx.arc(c.x, c.y, 60, 0, Math.PI * 2);
          ctx.fillStyle = g;
          ctx.fill();
        }
      }
      absorbGlow *= 0.93;
      for (let i = parts.length - 1; i >= 0; i--) {
        const p = parts[i];
        if (!c) {
          parts.splice(i, 1);
          continue;
        }
        const dx = c.x - p.x;
        const dy = c.y - p.y;
        const d = Math.hypot(dx, dy) || 1;
        p.vx += (dx / d) * 0.32;
        p.vy += (dy / d) * 0.32;
        if (d < 40) {
          p.vx *= 0.82;
          p.vy *= 0.82;
          p.r = Math.max(p.r * 0.96, 0.3);
        }
        const sp = Math.hypot(p.vx, p.vy);
        if (sp > 4.2) {
          p.vx = (p.vx / sp) * 4.2;
          p.vy = (p.vy / sp) * 4.2;
        }
        p.x += p.vx;
        p.y += p.vy;
        p.life += 1;
        const j = Math.min(Math.max(1 - d / p.d0, 0), 1);
        p.trail.push({ x: p.x, y: p.y, j });
        if (p.trail.length > 6) p.trail.shift();
        if (p.life > p.maxLife || d < 8) {
          if (d < 8) absorbGlow += 0.1;
          parts.splice(i, 1);
          continue;
        }
        const a = (1 - p.life / p.maxLife) * 0.7;
        const ang = Math.atan2(p.vy, p.vx);
        for (let t = 0; t < p.trail.length; t++) {
          const tp = p.trail[t];
          const tf = (t + 1) / p.trail.length;
          drawGasBlob(ctx, flameSoftSprite(tp.j), tp.x, tp.y, p.r * 14 * (0.4 + 0.6 * tf), ang, 2.2, a * tf * 0.3);
          drawGasBlob(ctx, flameSprite(tp.j), tp.x, tp.y, p.r * 6.5 * (0.5 + 0.5 * tf), ang, 2, a * tf * 0.55);
        }
      }
      ctx.globalCompositeOperation = 'source-over';
      raf = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      document.removeEventListener('mouseover', over);
      document.removeEventListener('mouseout', out);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      data-testid="hover-gas-canvas"
      style={{ position: 'fixed', inset: 0, zIndex: 9500, pointerEvents: 'none' }}
    />
  );
};

export default HoverGas;
